import React from 'react';
import { useTranslation } from 'react-i18next';
import { Popover, Box, ButtonBase, Divider, PopoverProps } from '@mui/material';
import { alpha, Theme } from '@mui/material/styles';
import CancelOutlinedIcon from '@mui/icons-material/CancelOutlined';
import { CARD_POPOVER_BG_SX } from '../../constants/cardPopover';

/**
 * カードから開くステータスメニューの器。ゲーム／アニメ／映画で共有する。
 *
 * 【なぜ汎用メニュー（common/PopoverMenu）ではないのか】
 * ここに並ぶ項目は「ステータス（排他選択）」「別UIを開く導線」「破壊的操作」の3種類だが、
 * 汎用メニューは全部を同じ行として1列に並べるしかなく、
 * ①縦長のリストになる ②押し間違いのコストの違いが形から読めない
 * ③`status` を持っているのに現在値を表示できない、が同時に起きていた。
 *
 * 【なぜメディアごとに分けないのか】
 * ステータスの集合だけが違って、器の作りは同じ。分けると3箇所に同じ調整が必要になる。
 * 違いは `statusItems` で外から渡す：
 *   ゲーム 5個 / アニメ 5個 / 映画 3個（コードは 1・4・5 で連番でない）
 *
 * 【このファイルの rem は 1rem = 20px】
 * `index.css` が `html { font-size: 20px }` を指定している。MUI の spacing(8px単位)は px だが、
 * **`fontSize` の rem は一般的な 16px 基準より 1.25 倍になる**。
 * `segmented` は 60px 弱の枠に文字を入れる密度なので、16px 基準で見積もると溢れて
 * 省略記号に落ちる。目安: 0.55rem = 11px、全角1文字 ≈ 11px。
 * → **`statusItems.label` は全角4文字までに収めること**（5文字が入る幅しかない）。
 *   完全な文言は `fullLabel` に置く（縦リストと読み上げがそちらを使う）。
 *
 * 【文字サイズに `Typography` を使わないこと】
 * テーマの `body1` / `caption` は `@media (max-width:600px)` を内側に持っている（`theme.ts`）。
 * variant のメディアクエリと `sx` の素の `fontSize` がぶつかる形にすると、どちらが勝つかを
 * 毎回確かめる必要が出る。variant を持たない `Box component="span"` で競合を作らない。
 */

export interface CardStatusItem {
  code: number;
  icon: React.ReactElement;
  /** セグメントに出す短いラベル。**全角4文字まで**（幅の理由は上のコメント参照） */
  label: string;
  /** 縦リストと読み上げに使う完全な文言。省略時は `label` を使う */
  fullLabel?: string;
  /**
   * ステータスの意味色。テーマトークンを引けるよう関数で受ける
   * （`info.main` 等は `theme.palette` からしか解決できない）。
   */
  color: (theme: Theme) => string;
}

export interface CardStatusMenuAction {
  icon: React.ReactNode;
  label: string;
  onClick: () => void;
}

interface CardStatusMenuProps {
  open: boolean;
  /** 左クリックは要素、右クリックはカーソル位置の仮想アンカー要素が入る */
  anchorEl: PopoverProps['anchorEl'];
  onClose: () => void;
  /** 何に対する操作かの明示。右クリックはカーソル位置に開くので対象が分からなくなりやすい */
  title: string;
  statusItems: CardStatusItem[];
  status: number | null;
  onSelectStatus: (status: number) => void;
  /**
   * `segmented`: ステータスを横並びにし、下に編集導線を並べる。項目数が多い場面用。
   * `list`: ステータスを素直な縦リストにする。**ステータス以外にほぼ項目が無い場面用**
   * （検索・一覧など未登録の作品。畳む対象が無いので横並びにする利点が無い）。
   */
  variant?: 'segmented' | 'list';
  /** 別UIを開く導線。ステータス設定とは押し間違いのコストが違うので面を分けて置く */
  actions?: CardStatusMenuAction[];
  onDelete?: () => void;
  /** 省略時は common の 'delete' を使う */
  deleteLabel?: string;
  /** 全操作を不可にする（アニメ・映画のアーリーアクセス制限） */
  disabled?: boolean;
  /** 最下部の注記。アーリーアクセスの案内など */
  footer?: React.ReactNode;
  anchorOrigin?: PopoverProps['anchorOrigin'];
  transformOrigin?: PopoverProps['transformOrigin'];
}

/** 1行に収まらなかったときに溢れさせず省略するための共通指定 */
const CLAMP_SX = {
  minWidth: 0,
  overflow: 'hidden',
  textOverflow: 'ellipsis',
  whiteSpace: 'nowrap',
} as const;

const CardStatusMenu: React.FC<CardStatusMenuProps> = ({
  open,
  anchorEl,
  onClose,
  title,
  statusItems,
  status,
  onSelectStatus,
  variant = 'segmented',
  actions = [],
  onDelete,
  deleteLabel,
  disabled = false,
  footer,
  anchorOrigin = { vertical: 'bottom', horizontal: 'right' },
  transformOrigin = { vertical: 'top', horizontal: 'center' },
}) => {
  // 器そのものの文言（ステータス群のラベル・削除の既定文言）は common から引く。
  // ここに日本語を直書きすると、メディア側を i18n にしても英語で崩れる。
  const { t } = useTranslation('common');

  const handleSelect = (code: number) => {
    // 既に選ばれているものを押しても同じ値を送るだけなので、閉じるだけにする。
    // 解除は「削除」（レビュー・スコアも消えるため確認を挟む）に一本化してある。
    if (code === status) {
      onClose();
      return;
    }
    onSelectStatus(code);
  };

  /**
   * ステータス：横並びのセグメント。現在値もここで示す。
   *
   * 選択の有無に関わらずアイコンは意味色で出す（色そのものが「どれが何か」の符号なので、
   * 非選択を無彩色にすると探すのに一度ラベルを読む必要が出る）。
   * 選択状態は色ではなく**面（背景＋枠）とラベルの色**で表す。
   */
  const renderSegmented = () => (
    // gap は分割後の幅を稼ぐため最小限（1px）。ここを広げるとラベルが省略記号に落ちる
    <Box sx={{ display: 'flex', gap: 0.125 }} role="group" aria-label={t('status')}>
      {statusItems.map((item) => {
        const selected = status === item.code;
        return (
          <ButtonBase
            key={item.code}
            onClick={() => handleSelect(item.code)}
            disabled={disabled}
            aria-pressed={selected}
            aria-label={item.fullLabel ?? item.label}
            sx={(theme) => {
              const color = item.color(theme);
              return {
                flex: 1,
                minWidth: 0,
                flexDirection: 'column',
                gap: 0.375,
                py: 0.875,
                // 分割後の枠に全角4〜5文字を入れるため、左右の余白は取らない。
                // ラベルは省略記号で受けるようにしてあるが、狭い端末で切れない幅を優先する。
                px: 0,
                borderRadius: 0.75,
                border: '1px solid',
                borderColor: selected ? alpha(color, 0.5) : 'transparent',
                backgroundColor: selected ? alpha(color, 0.16) : 'transparent',
                color,
                transition: 'background-color .15s ease, border-color .15s ease',
                '&:hover': {
                  backgroundColor: selected ? alpha(color, 0.22) : 'rgba(255, 255, 255, 0.06)',
                },
                '&.Mui-disabled': { opacity: 0.45 },
              };
            }}
          >
            {React.cloneElement(item.icon, {
              sx: { fontSize: '1.1rem', opacity: selected ? 1 : 0.9 },
            })}
            {/* ButtonBase は align-items: center なので、幅を渡さないと max-content のまま
                ボタンの外へ広がる（省略記号は自分のボックス基準なので効かない） */}
            <Box
              component="span"
              sx={{
                ...CLAMP_SX,
                display: 'block',
                width: '100%',
                textAlign: 'center',
                fontSize: '0.55rem', // 11px
                fontWeight: 600,
                lineHeight: 1,
                letterSpacing: '-0.01em',
                // ラベルまで全部色を付けると横一列が騒がしくなるので、色は選択中だけ
                color: selected ? 'inherit' : 'text.secondary',
              }}
            >
              {item.label}
            </Box>
          </ButtonBase>
        );
      })}
    </Box>
  );

  /** ステータス：縦リスト。畳む対象が無い場面ではこちらの方が素直に読める */
  const renderList = () => (
    <Box sx={{ display: 'flex', flexDirection: 'column', gap: 0.125 }} role="group" aria-label={t('status')}>
      {statusItems.map((item) => {
        const selected = status === item.code;
        return (
          <ButtonBase
            key={item.code}
            onClick={() => handleSelect(item.code)}
            disabled={disabled}
            aria-pressed={selected}
            sx={(theme) => {
              const color = item.color(theme);
              return {
                width: '100%',
                justifyContent: 'flex-start',
                gap: 1,
                px: 0.875,
                py: 0.75,
                borderRadius: 0.75,
                color,
                backgroundColor: selected ? alpha(color, 0.14) : 'transparent',
                transition: 'background-color .15s ease',
                '&:hover': {
                  backgroundColor: selected ? alpha(color, 0.2) : 'rgba(255, 255, 255, 0.06)',
                },
                '&.Mui-disabled': { opacity: 0.45 },
              };
            }}
          >
            {React.cloneElement(item.icon, {
              sx: { fontSize: '1.1rem', flexShrink: 0, opacity: selected ? 1 : 0.9 },
            })}
            <Box
              component="span"
              sx={{
                ...CLAMP_SX,
                fontSize: '0.725rem',
                fontWeight: 500,
                lineHeight: 1.2,
                // 本文として読ませる列なので、非選択は意味色を乗せない（アイコン側が符号を持つ）
                color: selected ? 'inherit' : 'text.primary',
              }}
            >
              {/* 幅に余裕がある列なので完全な文言を出す */}
              {item.fullLabel ?? item.label}
            </Box>
          </ButtonBase>
        );
      })}
    </Box>
  );

  return (
    <Popover
      open={open}
      anchorEl={anchorEl}
      onClose={onClose}
      anchorOrigin={anchorOrigin}
      transformOrigin={transformOrigin}
      slotProps={{
        paper: {
          sx: {
            // 固定 px にすると狭い端末で画面外へ出る。上限だけ決めて画面幅に従わせる。
            // list は横並びの幅要求が無いので細くしてよい。
            //
            // segmented のセグメント1つあたりの幅は (W - border2 - padding12 - gap) / 個数 - border2。
            // 5分割・320px で 58px、320px端末(W=308) で 56px。ラベル(全角5文字≈54.5px)が
            // ぎりぎり収まる寸法なので、**padding / gap / width を緩めるときは
            // ラベルの省略が復活しないか確認すること。**
            width: variant === 'segmented' ? 'min(320px, calc(100vw - 12px))' : 'min(232px, calc(100vw - 16px))',
            mt: 0.75,
            p: 0.75,
            // ぼかし・枠・角丸は theme.ts の MuiPopover から継承。背景だけカード用に濃くする
            ...CARD_POPOVER_BG_SX,
          },
        },
      }}
    >
      <Box
        sx={{
          ...CLAMP_SX,
          px: 0.75,
          pb: 0.75,
          fontSize: '0.7rem',
          fontWeight: 600,
          lineHeight: 1.4,
          color: 'text.secondary',
        }}
      >
        {title}
      </Box>

      {variant === 'segmented' ? renderSegmented() : renderList()}

      {actions.length > 0 && (
        <>
          <Divider sx={{ my: 0.75, borderColor: 'rgba(255, 255, 255, 0.08)' }} />
          <Box
            sx={{
              display: 'grid',
              // list は幅が細いので1列。segmented は2列に詰める
              gridTemplateColumns: variant === 'segmented' ? '1fr 1fr' : '1fr',
              gap: 0.5,
            }}
          >
            {actions.map((action, index) => (
              <ButtonBase
                key={action.label}
                onClick={action.onClick}
                disabled={disabled}
                sx={{
                  // グリッド項目は min-width: auto のままだと中身の幅で押し広げる
                  minWidth: 0,
                  // 2列で奇数個のときは最後を全幅にして、右側に穴が空くのを避ける
                  gridColumn: variant === 'segmented' && actions.length % 2 === 1 && index === actions.length - 1
                    ? 'span 2'
                    : undefined,
                  justifyContent: 'flex-start',
                  gap: 0.625,
                  px: 0.875,
                  py: 0.75,
                  borderRadius: 0.75,
                  color: 'text.secondary',
                  backgroundColor: 'rgba(255, 255, 255, 0.04)',
                  transition: 'background-color .15s ease, color .15s ease',
                  '&:hover': {
                    backgroundColor: 'rgba(255, 255, 255, 0.09)',
                    color: 'text.primary',
                  },
                  '&.Mui-disabled': { opacity: 0.45 },
                }}
              >
                {/* アイコン枠は固定幅にすること。中身は MUI アイコン(svg)と外部サービスの
                    ロゴ(img)が混ざって実寸が違うため、内容任せにするとラベルの左端がずれる。 */}
                <Box
                  sx={{
                    flexShrink: 0,
                    width: '1rem',
                    height: '1rem',
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'center',
                    '& > svg': { fontSize: '1rem' },
                  }}
                >
                  {action.icon}
                </Box>
                <Box
                  component="span"
                  sx={{ ...CLAMP_SX, fontSize: '0.675rem', fontWeight: 500, lineHeight: 1.2 }}
                >
                  {action.label}
                </Box>
              </ButtonBase>
            ))}
          </Box>
        </>
      )}

      {/* 破壊的操作は面を分けて右端に置く。以前はステータスの数行下に同じ見た目で並んでいた */}
      {onDelete && (
        <>
          <Divider sx={{ my: 0.75, borderColor: 'rgba(255, 255, 255, 0.08)' }} />
          <Box sx={{ display: 'flex', justifyContent: 'flex-end' }}>
            <ButtonBase
              onClick={onDelete}
              disabled={disabled}
              sx={{
                gap: 0.5,
                px: 0.875,
                py: 0.5,
                borderRadius: 0.75,
                color: 'error.main',
                opacity: 0.75,
                transition: 'background-color .15s ease, opacity .15s ease',
                '&:hover': {
                  backgroundColor: 'rgba(244, 67, 54, 0.12)',
                  opacity: 1,
                },
                '&.Mui-disabled': { opacity: 0.4 },
              }}
            >
              <CancelOutlinedIcon sx={{ fontSize: '0.9rem' }} />
              <Box component="span" sx={{ fontSize: '0.675rem', fontWeight: 500, lineHeight: 1.2 }}>
                {deleteLabel ?? t('delete')}
              </Box>
            </ButtonBase>
          </Box>
        </>
      )}

      {footer && (
        <>
          <Divider sx={{ my: 0.75, borderColor: 'rgba(255, 255, 255, 0.08)' }} />
          {footer}
        </>
      )}
    </Popover>
  );
};

export default CardStatusMenu;
